import { MoreVert } from "@mui/icons-material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { FeatureCollection } from "@turf/turf";
import { useNotification } from "app/NotificationProvider";
import { mixpanel } from "common/analytics";
import { BottomSheetHeader, PageTitleBar } from "common/components";
import { useTranslation } from "common/locales";
import { FieldEditActionMenu } from "features/fieldDetail";
import LivestockSection from "features/fieldDetail/LivestockSection";
import TotalCoverFeedback from "features/fieldDetail/TotalCoverFeedback";
import AfterFieldCreatedDialog from "features/fieldEdit/AfterFieldCreatedDialog";
import { FieldEventList } from "features/fieldEventList";
import { moveAnimalsIn } from "features/singleCoverInput/moveAnimalsIn";
import {
  Field,
  FIELD_TYPE,
  getAnimalGroupColorFromArray,
  getArableFieldsFilteredFromArray,
  getCurrentFieldWithAnimalsFromArray,
  getDemandFromArray,
  getGrainFieldsFromArray,
  getPastureFieldsFromArray,
  getSilageFieldsFromArray,
  GRASS_TYPE,
  useArableFields,
  useCurrentFarm,
  useCurrentFarmAnimalGroups,
  useCurrentFarmStockClasses,
  useFields,
  useUpdateField,
} from "model";
import { useAnimalGroup } from "model/useAnimalGroupQueries";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useUIStore } from "stores";

const BELLFLOWER_COLOR = "#D3C9E7";

interface MetricItemProps {
  label: string;
  value: string | number;
  unit?: string;
  warning?: boolean;
}

function MetricItem({ label, value, unit, warning }: MetricItemProps) {
  return (
    <Tooltip title={String(value)} enterDelay={500} enterNextDelay={500}>
      <Box
        sx={{
          py: 0.75,
          borderBottom: "1px solid #F0F0F0",
          "&:last-child": {
            borderBottom: "none",
          },
          minWidth: 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", display: "block", fontSize: "0.7rem" }}
        >
          {label}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            gap: 0.5,
            minWidth: 0,
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              color: warning ? "#DC2626" : "#1a1a2e",
              fontStyle: warning ? "italic" : "normal",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {value}
          </Typography>
          {unit && (
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontSize: "0.7rem",
                flexShrink: 0,
              }}
            >
              {unit}
            </Typography>
          )}
        </Box>
      </Box>
    </Tooltip>
  );
}

interface MetricsCardProps {
  children: React.ReactNode;
}

function MetricsCard({ children }: MetricsCardProps) {
  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: 2,
        px: 1.5,
        py: 0.5,
        border: `1px solid ${BELLFLOWER_COLOR}`,
      }}
    >
      {children}
    </Box>
  );
}

interface WarningItemProps {
  label: string;
  action: React.ReactNode;
}

function WarningItem({ label, action }: WarningItemProps) {
  return (
    <Box
      sx={{
        py: 0.75,
        px: 1,
        mx: -1,
        borderBottom: "1px solid #F7E880",
        backgroundColor: "#FDFBEB",
        borderRadius: 1,
      }}
    >
      <Typography
        variant="caption"
        sx={{ color: "#A48137", display: "block", fontSize: "0.7rem" }}
      >
        {label}
      </Typography>
      {action}
    </Box>
  );
}

interface Props {
  field: Field;
}

interface LocationState {
  previousPath?: string;
  previousSearch?: string;
}

export default function FieldDetails({ field }: Props) {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [grassTypeDialogOpen, setGrassTypeDialogOpen] = useState(false);
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const location = useLocation<LocationState>();
  const { data: currentFarm } = useCurrentFarm();
  const iconSize = "medium";

  // UI state from Zustand
  const setZoomToGeom = useUIStore((state) => state.setZoomToGeom);
  const openMoveOutEventDialog = useUIStore(
    (state) => state.openMoveOutEventDialog
  );

  // Use React Query hooks for animal group and demand
  const { data: animalGroup } = useAnimalGroup(field.animalGroup ?? undefined);

  // Get stock classes and calculate demand
  const { data: stockClasses } = useCurrentFarmStockClasses();
  const animalGroupStockClasses = useMemo(
    () => stockClasses.filter((sc) => sc.animalGroup === animalGroup?.guid),
    [stockClasses, animalGroup?.guid]
  );
  const demand = getDemandFromArray(animalGroupStockClasses);

  // Use proper React Query hook for animal groups
  const { data: animalGroups } = useCurrentFarmAnimalGroups();

  // Get animal group color for visual accent
  const animalGroupColor = useMemo(
    () =>
      field.animalGroup
        ? animalGroup?.color ||
          getAnimalGroupColorFromArray(animalGroups, field.animalGroup)
        : null,
    [animalGroups, field.animalGroup, animalGroup?.color]
  );

  // Use React Query for fields data
  const { data: allGrassFields = [] } = useFields();
  const { data: allArableFields = [] } = useArableFields();

  // Compute field lists for navigation logic using helper functions
  const grainFields = useMemo(
    () => getGrainFieldsFromArray(allArableFields),
    [allArableFields]
  );
  const arableFields = useMemo(
    () => getArableFieldsFilteredFromArray(allArableFields),
    [allArableFields]
  );
  const silageFields = useMemo(
    () => getSilageFieldsFromArray(allGrassFields),
    [allGrassFields]
  );
  const pastureFields = useMemo(
    () => getPastureFieldsFromArray(allGrassFields),
    [allGrassFields]
  );

  const updateFieldMutation = useUpdateField();
  const { notify } = useNotification();

  const fieldGeom = useMemo(
    (): FeatureCollection => ({
      type: "FeatureCollection",
      features: [field.geom],
    }),
    [field.geom]
  );

  const overgrazed = Boolean(
    animalGroup?.postGrazingTarget &&
      field.dryMatterNow &&
      field.dryMatterNow < animalGroup.postGrazingTarget
  );

  let availableDryMatter;
  if (animalGroup && animalGroup.postGrazingTarget) {
    availableDryMatter = Math.floor(
      ((field.dryMatterNow - animalGroup.postGrazingTarget) * field.area) /
        10000
    );
    if (availableDryMatter < 0) {
      availableDryMatter = 0;
    }
  }

  let grazingDaysAhead;
  if (availableDryMatter && demand) {
    grazingDaysAhead = availableDryMatter / demand;
  }

  // Compute field with animals using React Query data
  const currentFieldWithAnimals = useMemo(
    () =>
      getCurrentFieldWithAnimalsFromArray(allGrassFields, field.animalGroup),
    [allGrassFields, field.animalGroup]
  );

  const canMoveIn =
    field.animalGroup && field.guid !== currentFieldWithAnimals?.guid;

  // Track which field we've zoomed to, so we only zoom once per field
  const zoomedToFieldRef = useRef<string | null>(null);

  useEffect(() => {
    // Only zoom if we haven't already zoomed to this field
    if (zoomedToFieldRef.current !== field.guid) {
      zoomedToFieldRef.current = field.guid;
      setZoomToGeom(fieldGeom);
    }
  }, [field.guid, fieldGeom, setZoomToGeom]);

  const handleBack = () => {
    if (location.state?.previousPath) {
      navigate({
        pathname: location.state.previousPath,
        search: location.state.previousSearch || "",
      });
      return;
    }

    // If field belongs to an animal group, navigate to that animal group
    if (field.animalGroup) {
      navigate(`/animal-group/${field.animalGroup}`);
      return;
    }

    // Otherwise, determine field type and navigate to appropriate fields list
    const isGrainField = grainFields.some((f) => f?.guid === field.guid);
    if (isGrainField) {
      navigate("/fields-list/grain");
      return;
    }

    const isArableField = arableFields.some((f) => f?.guid === field.guid);
    if (isArableField) {
      navigate("/fields-list/arable");
      return;
    }

    const isSilageField = silageFields.some((f) => f?.guid === field.guid);
    if (isSilageField) {
      navigate("/fields-list/silage");
      return;
    }

    const isPastureField = pastureFields.some((f) => f?.guid === field.guid);
    if (isPastureField) {
      navigate("/fields-list/pasture");
      return;
    }

    // Fallback to farm
    backToFarm();
  };

  const backToFarm = () => {
    if (currentFarm?.guid) {
      navigate(`/farm/${currentFarm.guid}`);
    }
  };

  async function onMoveIn() {
    try {
      if (field) {
        if (currentFieldWithAnimals) {
          openMoveOutEventDialog({
            open: true,
            newFieldWithAnimals: field,
            currentFieldWithAnimals: currentFieldWithAnimals,
          });
        } else {
          await moveAnimalsIn(
            field,
            currentFieldWithAnimals,
            new Date(),
            notify,
            t
          );
        }
      }
    } catch (e) {
      console.log(e);
    }
  }

  async function handleGrassTypeChange(
    value: GRASS_TYPE | undefined,
    field: Field
  ) {
    if (value && field) {
      try {
        console.log(value);
        console.log(field);
        //field.grassType = value;
        //console.log("after assign");
        await updateFieldMutation.mutateAsync({
          ...field,
          grassType: value, // Ensure grassType is updated
        });
        console.log("after mutation");
        mixpanel.track("Field grass type updated", {
          fieldGuid: field.guid,
          grassType: value,
        });
      } catch (e) {
        console.error("Failed to update grass type", e);
        notify(t("fieldinput.grassType.updateFailed"), "error");
      }
    }
  }
  // Determine field type prefix
  const isGrainField = grainFields.some((f) => f?.guid === field.guid);
  const isArableField = arableFields.some((f) => f?.guid === field.guid);

  const getFieldTypePrefix = () => {
    if (field.animalGroup) {
      return t("field.prefix.grazing");
    }
    if (isGrainField) {
      return t("field.prefix.grain");
    }
    if (isArableField) {
      return t("field.prefix.arable");
    }
    // Default for grass fields without animal group (silage)
    return t("field.prefix.silage");
  };

  const fieldTypePrefix = getFieldTypePrefix();

  // Header right actions
  const headerRightAction = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <IconButton
        onClick={openMenu}
        size="small"
        sx={{
          backgroundColor: "#F1EFF7",
          color: "#7D5DA3",
          "&:hover": { backgroundColor: "#E5E1F0" },
        }}
      >
        <MoreVert />
      </IconButton>
    </Box>
  );

  return (
    <>
      {isMobile ? (
        <BottomSheetHeader
          titlePrefix={`${fieldTypePrefix}:`}
          title={field.name}
          onBack={handleBack}
          rightAction={headerRightAction}
        />
      ) : (
        <PageTitleBar
          titlePrefix={`${fieldTypePrefix}:`}
          title={field.name}
          onBack={handleBack}
        >
          {headerRightAction}
        </PageTitleBar>
      )}

      {/* Animal Group Color Accent Strip */}
      {animalGroupColor && (
        <Box
          sx={{
            height: 4,
            backgroundColor: animalGroupColor,
            width: "100%",
          }}
        />
      )}

      {/* Main Content */}
      <Box sx={{ pb: 2, backgroundColor: "white" }}>
        <Box
          sx={{
            backgroundColor: "white",
            px: 3,
            pt: animalGroupColor ? 2 : 0,
          }}
        >
          {/* Field Metrics */}
          <Box sx={{ mb: 2 }}>
            {/* Total Cover Feedback Box - shows overgrazed warning integrated */}
            <TotalCoverFeedback field={field} overgrazed={overgrazed} />
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 1,
              }}
            >
              {/* First card: Area, Grass Type */}
              <MetricsCard>
                <MetricItem
                  label={t("fieldinput.area.text")}
                  value={field ? (field.area / 10000).toFixed(2) : "—"}
                  unit="ha"
                />
                {field.grassType ? (
                  <MetricItem
                    label={t("fieldinput.grassType.text")}
                    value={t(`fieldinput.grassType.option.${field.grassType}`)}
                  />
                ) : (
                  <WarningItem
                    label={t("fieldinput.grassType.text")}
                    action={
                      <Button
                        size="small"
                        onClick={() => setGrassTypeDialogOpen(true)}
                        sx={{
                          color: "#A48137",
                          textTransform: "none",
                          fontWeight: 600,
                          fontSize: "0.75rem",
                          padding: "2px 8px",
                          minWidth: "auto",
                          minHeight: "auto",
                          backgroundColor: "rgba(164, 129, 55, 0.1)",
                          "&:hover": {
                            backgroundColor: "rgba(164, 129, 55, 0.2)",
                          },
                        }}
                      >
                        {t("fieldDetail.setGrassTypeButton")}
                      </Button>
                    }
                  />
                )}
              </MetricsCard>

              {/* Second card: Available DM, Grazing Days */}
              <MetricsCard>
                {animalGroup && animalGroup.postGrazingTarget ? (
                  <MetricItem
                    label={t("fieldDetail.availableDryMatter")}
                    value={
                      field && !field.invalidatedTimestamp
                        ? availableDryMatter || 0
                        : "—"
                    }
                    unit="kgDM"
                  />
                ) : (
                  <MetricItem
                    label={t("fieldDetail.availableDryMatter")}
                    value="—"
                  />
                )}
                <MetricItem
                  label={t("fieldDetail.grazingDaysAhead")}
                  value={
                    field && !field.invalidatedTimestamp && grazingDaysAhead
                      ? `${Math.floor(grazingDaysAhead)}d ${Math.round(
                          (grazingDaysAhead % 1) * 24
                        )}h`
                      : "—"
                  }
                />
              </MetricsCard>
            </Box>
          </Box>

          {/* Livestock Section */}
          <LivestockSection
            field={field}
            onGroupChange={moveToAnimalGroup}
            canMoveIn={!!canMoveIn}
            onMoveIn={onMoveIn}
            animalGroupColor={animalGroupColor}
          />

          {/* Field Events */}
          <FieldEventList field={field} />
        </Box>

        {/* Grass Type Selection Dialog */}
        <Dialog
          open={grassTypeDialogOpen}
          onClose={() => setGrassTypeDialogOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ pb: 1 }}>
            {t("fieldDetail.selectGrassType")}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {Object.entries(GRASS_TYPE).map(([key, value]) => (
                <Button
                  key={key}
                  variant="outlined"
                  fullWidth
                  onClick={async () => {
                    setGrassTypeDialogOpen(false);
                    await handleGrassTypeChange(value, field);
                  }}
                  sx={{
                    justifyContent: "flex-start",
                    borderColor: "#E0E0E0",
                    color: "text.primary",
                    textTransform: "none",
                    py: 1.5,
                    "&:hover": {
                      borderColor: "#2E7831",
                      backgroundColor: "#F7FCF1",
                    },
                  }}
                >
                  {t(`fieldinput.grassType.option.${value}`)}
                </Button>
              ))}
            </Box>
          </DialogContent>
        </Dialog>

        {/* Field Edit Action Menu */}
        <FieldEditActionMenu
          anchor={menuAnchor}
          close={closeMenu}
          fieldId={field.guid || ""}
          fieldType={FIELD_TYPE.GRASS}
        />
      </Box>

      <AfterFieldCreatedDialog />
    </>
  );

  function openMenu(event: React.MouseEvent<HTMLButtonElement>) {
    setMenuAnchor(event.currentTarget);
  }

  function closeMenu() {
    setMenuAnchor(null);
  }
  async function moveToAnimalGroup(guid?: string) {
    const animalGroupName = animalGroups.find((ag) => ag.guid === guid)?.name;
    try {
      await updateFieldMutation.mutateAsync({ ...field, animalGroup: guid });
      mixpanel.track("FieldAssign successful");
      notify(
        guid
          ? t("fieldlistmenu.notification.movedAnimalGroup", {
              fieldName: field?.name,
              animalGroupName: animalGroupName,
            })
          : t("fieldlistmenu.notification.movedToSilage", {
              fieldName: field?.name,
            })
      );
    } catch (e) {
      mixpanel.track("FieldAssign failed");
      notify(t("fieldlistmenu.notification.moveFailed"), "error");
    }
  }
}

// Removed unused styled components - now using PageTitleBar
/* const Header = styled.div`
  background-color: white;
  padding: 16px;
  padding-top: calc(env(safe-area-inset-top) + 16px);
  display: flex;
  align-items: center;
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.1);
  position: relative;
`;

const BackButton = styled(IconButton)`
  position: absolute;
  left: 8px;
`;

const TitleContainer = styled.div`
  flex: 1;
  text-align: center;
  margin: 0 48px;
`; */

const TooltipChild = styled("div")({
  width: "100%",
});

interface MoveAnimalsButtonProps {
  canMoveIn: boolean;
  onMoveIn: () => Promise<void>;
  fieldHasAnimalGroup: boolean;
}

function MoveAnimalsButton({
  canMoveIn,
  onMoveIn,
  fieldHasAnimalGroup,
}: MoveAnimalsButtonProps) {
  const { t } = useTranslation();
  const [isSendingMoveRequest, setSendingMoveRequest] = useState(false);

  const disabled = isSendingMoveRequest || !canMoveIn;

  const handleButtonClick = async () => {
    if (disabled) return;
    setSendingMoveRequest(true);
    await onMoveIn();
    setSendingMoveRequest(false);
  };

  const MoveButton = (
    <Button
      variant="outlined"
      endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
      onClick={handleButtonClick}
      disabled={disabled}
      sx={{
        height: 36,
        fontSize: "14px",
        fontWeight: 500,
        textTransform: "none",
        borderColor: "#2E7831",
        color: "#2E7831",
        borderRadius: "8px",
        px: 2,
        "&:hover": {
          borderColor: "#1C4321",
          backgroundColor: "#F7FCF1",
        },
        "&.Mui-disabled": {
          borderColor: "#E0E0E0",
          color: "#9E9E9E",
        },
      }}
    >
      {t("fieldview.moveAnimals.text")}
    </Button>
  );

  return canMoveIn ? (
    MoveButton
  ) : (
    <Tooltip
      title={
        fieldHasAnimalGroup
          ? t("fieldlistmenu.moveIn.tooltip.sameField")
          : t("fieldlistmenu.moveIn.tooltip.silage")
      }
    >
      <TooltipChild>{MoveButton}</TooltipChild>
    </Tooltip>
  );
}
